import type { Alarm, SiteSnapshot, SiteTopology } from "@/data/types";
import type { HistorySeries } from "@/components/charts/HistoryChart";

/**
 * Export the recent[] snapshot buffer as a CSV file the operator can
 * open in Excel / share with engineering. Columns:
 *
 *   ts_iso, grid_kW, net_building_load_kW, pv_kW, net_at_meter_kW,
 *   bess_aggregate_kW, bess_soc_avg_pct, bess_online_count,
 *   bess_<n>_kW, bess_<n>_soc_pct, bess_<n>_setpoint_kW, ... (per unit)
 *
 * One row per snapshot, ordered by ts ascending.
 */
export function exportSnapshotsCsv(
  snapshots: SiteSnapshot[],
  topology: SiteTopology,
  filenameStem: string,
): void {
  if (!snapshots.length) return;
  const sorted = [...snapshots].sort((a, b) => a.ts - b.ts);
  const bessIds = topology.bess.map((b) => b.id);
  const bessNames = topology.bess.map((b) => b.name);

  const header = [
    "ts_iso",
    "grid_kW",
    "net_building_load_kW",
    "pv_kW",
    "net_at_meter_kW",
    "bess_aggregate_kW",
    "bess_soc_avg_pct",
    "bess_online_count",
    ...bessIds.flatMap((_, i) => [
      `${bessNames[i]}_kW`,
      `${bessNames[i]}_soc_pct`,
      `${bessNames[i]}_setpoint_kW`,
      `${bessNames[i]}_temp_C`,
    ]),
  ];

  const rows: string[] = [];
  rows.push(header.join(","));
  for (const s of sorted) {
    const row: (string | number)[] = [
      new Date(s.ts).toISOString(),
      kw(s.grid.active_W),
      kw(s.building_load_W),
      kw(s.pv?.active_W ?? 0),
      kw(s.net_at_meter_W),
      kw(s.bess_aggregate.active_W),
      round(s.bess_aggregate.soc_avg_pct, 1),
      s.bess_aggregate.online_count,
    ];
    for (const id of bessIds) {
      const u = s.bess_per_unit.find((x) => x.id === id);
      row.push(u ? kw(u.active_W) : "");
      row.push(u ? round(u.soc_pct, 1) : "");
      row.push(u ? kw(u.setpoint_W) : "");
      row.push(u && Number.isFinite(u.ambient_temperature_C) ? round(u.ambient_temperature_C, 1) : "");
    }
    rows.push(row.join(","));
  }

  downloadCsv(`${filenameStem || "operator-export"}.${tsStamp()}.csv`, rows.join("\n"));
}

/**
 * Export HistoryChart series as a CSV (HIST-06 / CONS-09) — the generic
 * exporter for Historical / BESS-detail / any chart that plots
 * `HistorySeries[]`.
 *
 * Columns: `ts_iso, <name (unit)>, …` (one per series, in given order).
 * Rows: the union of every series' timestamps, ascending; a series with no
 * sample at a timestamp gets a blank cell (no interpolation — the CSV is
 * forensic evidence, not a chart).
 *
 * The caller provides the full filename (convention:
 * `<site>-<page>-<start>-<end>.csv`); ".csv" is appended when missing.
 */
export function exportSeriesCsv(filename: string, series: HistorySeries[]): void {
  if (!series.length) return;

  const cols = series.map((s) => {
    const byTs = new Map<number, number>();
    for (const p of s.data) byTs.set(p.ts, p.value);
    return byTs;
  });
  const allTs = [...new Set(series.flatMap((s) => s.data.map((p) => p.ts)))].sort(
    (a, b) => a - b,
  );
  if (!allTs.length) return;

  const header = [
    "ts_iso",
    ...series.map((s) => csvCell(s.unit ? `${s.name} (${s.unit})` : s.name)),
  ];
  const lines: string[] = [header.join(",")];
  for (const ts of allTs) {
    const cells: string[] = [new Date(ts).toISOString()];
    for (const byTs of cols) {
      const v = byTs.get(ts);
      cells.push(v != null && Number.isFinite(v) ? String(v) : "");
    }
    lines.push(cells.join(","));
  }

  downloadCsv(filename.endsWith(".csv") ? filename : `${filename}.csv`, lines.join("\n"));
}

/**
 * Export the Revenue browser's in-memory `priced` buckets (N18) — the one
 * Analyze page where the export gesture had gone missing exactly where the
 * money lives. The caller passes the same array the chart plots, so the CSV
 * mirrors the on-screen figures (cap-clamped on coarse grans, billed on the
 * gran-dependent side) rather than raw meter counters.
 *
 * Columns: bucket_iso, charged_kWh, discharged_kWh, billable_kWh,
 * billed_side, price_eur_per_kWh, revenue_eur. `billed_side` and the price
 * are emitted so the €/handoff is reproducible — a finance reviewer can see
 * which half of the round-trip was billed on w/m/y buckets.
 *
 * The caller provides the full filename (convention:
 * `<site>-revenue-<gran>-<start>-<end>.csv`); ".csv" is appended when missing.
 */
export function revenueRowsCsv(
  filename: string,
  rows: {
    ts: number;
    charged_kWh: number;
    discharged_kWh: number;
    billable_kWh: number;
    billableSide: "charge" | "discharge";
    revenue: number;
  }[],
  price: number,
): void {
  if (!rows.length) return;
  const sorted = [...rows].sort((a, b) => a.ts - b.ts);
  const lines: string[] = [
    "bucket_iso,charged_kWh,discharged_kWh,billable_kWh,billed_side,price_eur_per_kWh,revenue_eur",
  ];
  for (const r of sorted) {
    lines.push([
      new Date(r.ts).toISOString(),
      round(r.charged_kWh, 3),
      round(r.discharged_kWh, 3),
      round(r.billable_kWh, 3),
      r.billableSide,
      round(price, 4),
      round(r.revenue, 2),
    ].join(","));
  }
  downloadCsv(filename.endsWith(".csv") ? filename : `${filename}.csv`, lines.join("\n"));
}

/**
 * Export the alarm console's CURRENTLY FILTERED rows (5.8-consumers) —
 * the caller passes exactly what the operator sees, so the CSV mirrors
 * the on-screen triage view (tab + device + severity + text filters).
 *
 * Columns: raised_iso, severity, status, device, message, ack_by,
 * cleared_iso. Device is the friendly name (rows show names, not ids —
 * CONS-13); blanks for never-acked / never-cleared alarms.
 */
export function exportAlarmsCsv(filenameStem: string, alarms: Alarm[]): void {
  if (!alarms.length) return;
  const lines: string[] = [
    "raised_iso,severity,status,device,message,ack_by,cleared_iso",
  ];
  for (const a of alarms) {
    lines.push([
      new Date(a.raised_at).toISOString(),
      csvCell(a.severity),
      csvCell(a.status),
      csvCell(a.device_name),
      csvCell(a.message),
      csvCell(a.ack_by ?? ""),
      a.cleared_at ? new Date(a.cleared_at).toISOString() : "",
    ].join(","));
  }
  downloadCsv(`${filenameStem || "alarms-export"}.${tsStamp()}.csv`, lines.join("\n"));
}

/** RFC-4180 escape — quote a cell containing comma / quote / newline. */
function csvCell(s: string): string {
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Shared download trigger — BOM-prefixed so Excel detects UTF-8. */
function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function kw(w: number): number { return round(w / 1000, 3); }
function round(n: number, d: number): number {
  if (!Number.isFinite(n)) return 0;
  const f = Math.pow(10, d);
  return Math.round(n * f) / f;
}
function tsStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}
